import {createContext, useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef} from 'react';
import type {JSX, ReactNode} from 'react';
import {workspaceReducer, initialWorkspaceState} from './reducer';
import {createWorkspaceActions} from './actions';
import type {WorkspaceContextValue} from './types';

const Context = createContext<WorkspaceContextValue | undefined>(undefined);

export function useWorkspace(): WorkspaceContextValue {
  const workspace = useContext(Context);
  if (!workspace) throw new Error('Workspace components require a WorkspaceProvider.');
  return workspace;
}

export function WorkspaceProvider({children}: {children: ReactNode}): JSX.Element {
  const [state, dispatch] = useReducer(workspaceReducer, undefined, initialWorkspaceState);
  const committedState = useRef(state);
  useLayoutEffect(() => { committedState.current = state; }, [state]);
  const actions = useMemo(() => createWorkspaceActions({dispatch, getState: () => committedState.current}), [dispatch]);
  useEffect(() => { void actions.retry(); }, [actions]);
  const value = useMemo<WorkspaceContextValue>(() => ({...state, ...actions}), [state, actions]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}